import { useLayoutEffect, useState, type RefObject } from 'react';

import type { Size } from '../../lib/geometry';

/**
 * The element's current content-box size.
 *
 * ADR-0004 requires the stage be measured rather than hard-coded, so the 402x436 and 880x616
 * figures from the handoff are layout targets rather than constants in the maths.
 *
 * Measured **synchronously on mount** and then kept current with a `ResizeObserver`. The
 * initial `getBoundingClientRect` matters: `ResizeObserver` delivers its first callback
 * during frame production, so relying on it alone leaves the stage empty until the first
 * frame — a visible flash normally, and indefinitely in any context that is not compositing
 * (a background tab, a hidden pane).
 *
 * Returns `null` only if the element is not mounted or has no size.
 */
export function useMeasuredSize(ref: RefObject<HTMLElement | null>): Size | null {
  const [size, setSize] = useState<Size | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;

    const apply = (w: number, h: number): void => {
      // Ignore the degenerate measurements a hidden or not-yet-laid-out element reports;
      // feeding those into containFit would throw.
      if (w <= 0 || h <= 0) return;
      setSize((previous) =>
        previous !== null && previous.w === w && previous.h === h ? previous : { w, h },
      );
    };

    const rect = element.getBoundingClientRect();
    apply(rect.width, rect.height);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;

      const box = entry.contentBoxSize[0];
      apply(
        box ? box.inlineSize : entry.contentRect.width,
        box ? box.blockSize : entry.contentRect.height,
      );
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}
